import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, Briefcase, Trash2 } from "lucide-react";

export default function HiringListDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [list, setList] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get(`/hiring-lists/${id}`);
            setList(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const remove = async (cid) => {
        try {
            await api.delete(`/hiring-lists/${id}/members/${cid}`);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const deleteList = async () => {
        if (!window.confirm("Delete this list? Candidates remain in the database.")) return;
        try {
            await api.delete(`/hiring-lists/${id}`);
            toast.success("List deleted");
            nav("/lists");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!list) return <div className="p-8 font-mono text-sm text-muted-foreground">Loading...</div>;

    return (
        <div className="max-w-[1600px] mx-auto p-3 sm:p-5 lg:p-6 space-y-8">
            <Link to="/lists" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-lists">
                <ChevronLeft className="w-4 h-4" /> All lists
            </Link>

            <header className="border-b border-border pb-6 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono mb-2">
                        <span className="w-2 h-2" style={{ backgroundColor: list.color || "#002FA7" }} />
                        <span style={{ color: list.color || "#002FA7" }}>Hiring list</span>
                    </div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl">{list.name}</h1>
                    {list.description && <p className="text-muted-foreground mt-2 max-w-2xl">{list.description}</p>}
                </div>
                <Button variant="outline" onClick={deleteList} className="rounded-none text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground" data-testid="delete-list">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete list
                </Button>
            </header>

            {(list.candidates || []).length === 0 ? (
                <div className="border border-dashed border-border p-12 text-center">
                    <h3 className="font-display font-bold text-lg">No candidates in this list yet</h3>
                    <p className="text-sm text-muted-foreground mt-2">Open a candidate and click <span className="font-mono">Add to list</span>.</p>
                </div>
            ) : (
                <div className="border border-border bg-white divide-y divide-border">
                    {list.candidates.map((c) => (
                        <div key={c.id} className="flex items-center justify-between p-4 hover:bg-secondary" data-testid={`list-member-${c.id}`}>
                            <Link to={`/candidates/${c.id}`} className="flex-1">
                                <div className="font-medium">{c.name || "Unknown"}</div>
                                <div className="text-xs text-muted-foreground font-mono">
                                    {c.current_title || "—"}{c.current_company ? ` @ ${c.current_company}` : ""}
                                    {c.location ? ` · ${c.location}` : ""}
                                </div>
                            </Link>
                            <button onClick={() => remove(c.id)} className="text-muted-foreground hover:text-destructive ml-3" data-testid={`remove-from-list-${c.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
